import { useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { REPORTER_PREFIX_PATTERN } from 'utils/routePatterns';

import { pathToRegexp } from 'path-to-regexp';
import type { Key } from 'path-to-regexp';

import { AppRoutePath, NOT_FOUND_CASE_ID } from 'config/constants';
import { getWARDCaseId } from 'domains/viewer/Viewer/Ward/utils';

const compileWorklistIdRoute = (path: string, param: string, end = true) => {
  const keys: Array<Key> = [];
  const regexp = pathToRegexp(path, keys, { end });
  return { regexp, group: keys.findIndex((key) => key.name === param) + 1 };
};

// Compiled once: extractWorklistIds runs for every open tab whenever the open-tab state changes.
const WORKLIST_ID_ROUTES = [
  compileWorklistIdRoute(AppRoutePath.VIEWER, 'worklistId'),
  compileWorklistIdRoute(AppRoutePath.REPORTER, 'worklistId', false),
  compileWorklistIdRoute(AppRoutePath.PATIENT_JACKET_UNDOCKED, 'smid'),
  compileWorklistIdRoute(AppRoutePath.PATIENT_JACKET, 'smid'),
];

export const extractWorklistIds = (path: string): string[] => {
  for (const { regexp, group } of WORKLIST_ID_ROUTES) {
    const id = regexp.exec(path)?.[group];
    if (id != null) return id !== NOT_FOUND_CASE_ID ? [id] : [];
  }
  return [];
};

const useWorklistId = (): Nullable<string> => {
  const matchViewer = useMatch(AppRoutePath.VIEWER);
  const matchReporter = useMatch(REPORTER_PREFIX_PATTERN);
  const matchAISandbox = useMatch(AppRoutePath.AI_SANDBOX);
  const matchPatientJacket = useMatch(AppRoutePath.PATIENT_JACKET);
  const matchUndockedPatientJacket = useMatch(AppRoutePath.PATIENT_JACKET_UNDOCKED);
  const matchWard = getWARDCaseId();
  const worklistId =
    matchViewer?.params?.worklistId ??
    matchReporter?.params?.worklistId ??
    matchAISandbox?.params?.worklistId ??
    matchPatientJacket?.params?.smid ??
    matchUndockedPatientJacket?.params?.smid ??
    matchWard ??
    '';

  const smid = useMemo(() => {
    return worklistId.length > 0 && worklistId !== 'undefined' ? worklistId : null;
  }, [worklistId]);

  return smid;
};

export default useWorklistId;
